<template>
  <div>
    <!-- No project memberships and finished loading -->
    <div v-if="!loading && projects.length === 0">
      <!-- Showing more guided version -->
      <page-title>Welcome to Synpse!</page-title>

      <v-row class="mt-2">
        <v-col class="d-flex" md="6" sm="12" xs="12">
          <v-card width="100%" class="mt-4">
            <v-card-title>Getting Started</v-card-title>
            <v-card-text>
              Synpse is a system that let's you organize your devices, perform maintenance
              actions on them and easily deploy applications in a stateless, cloud-native way.
              In Synpse, all your devices and applications belong to a project. Projects can
              be shared between users.
              <br/>
              <br/>
              Typical flow:
              <ol>
                <li>Create a project</li>
                <li>Add one or more devices to your project</li>
                <li>Deploy an application</li>
              </ol>
            </v-card-text>
            <v-card-text>
              Useful Resources:
              <ul>
                <li><a href="https://docs.synpse.net" rel="noopener" target="_blank">Documentation</a></li>
                <li><a href="https://discord.com/invite/dkgN4vVNdm" rel="noopener" target="_blank">Discord Community</a></li>
                <li><a href="https://github.com/synpse-hq/synpse" rel="noopener" target="_blank">Examples</a></li>
              </ul>
            </v-card-text>
            <v-card-actions v-if="myProjects && billingAccount">
              <v-spacer></v-spacer>
              <project-create                
                :projects="myProjects"
                :billingAccount="billingAccount"></project-create>
            </v-card-actions>
          </v-card>
        </v-col>

        <v-col class="d-flex" md="6" sm="12" xs="12">
          <v-card width="100%" class="mt-4">
            <v-card-title>What is Synpse?</v-card-title>
            <v-card-text>
              Synpse is a complete service to <a href="https://docs.synpse.net/devices/provisioning" rel="noopener" target="_blank">provision</a>,
              <a href="https://docs.synpse.net/synpse-core/devices" rel="noopener" target="_blank">manage devices</a> and
              <a href="https://docs.synpse.net/applications/deploy" rel="noopener" target="_blank">deploy applications</a>. We provide utilities to
              prepare images for bulk provisioning, filter and label your devices, conditionally deploy application
              on all or a subset of your devices.
            </v-card-text>
            <v-card-text>
              In Synpse you store your application configuration in a popular yaml format that can be easily stored
              in popular version control services like GitHub, Gitlab or Bitbucket. Sensitive application configuration
              can be stored in <a href="https://docs.synpse.net/applications/secrets" rel="noopener" target="_blank">secrets</a>.
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div v-else>
      <!-- Showing projects -->
      <v-layout>
        <div class="ml-4 mt-1 mb-2" v-if="myProjects && billingAccount">
          <project-create
            :projects="myProjects"
            :billingAccount="billingAccount"
          ></project-create>
        </div>
      </v-layout>

      <!-- Error message -->
      <!-- <v-layout class="ml-4">
        <div v-if="error">
          <v-alert type="error" dense outlined>
            {{ error }}
          </v-alert>
        </div>
      </v-layout> -->

      <v-layout row>
        <v-flex xs12>
          <v-subheader class="mt-5 ml-4 mb-2">
            My projects
          </v-subheader>
        </v-flex>              
      </v-layout>

      <v-layout class="mt-0 ml-4">
        <div class="tbcard">
          <div>            
            <ProjectList
              :projects="myProjects"
            ></ProjectList>
          </div>
        </div>
      </v-layout>

      <v-layout row v-if="sharedProjects.length > 0">
        <v-flex xs12>
          <v-subheader class="mt-10 ml-4 mb-2">
            Shared projects
          </v-subheader>
        </v-flex>              
      </v-layout>

      <v-layout class="mt-0 ml-4">
        <div class="tbcard">
          <div>            
            <ProjectList
              :projects="sharedProjects"
            ></ProjectList>
          </div>
        </div>
      </v-layout>
    </div>
  </div>
</template>

<style lang="stylus">
.tbcard {
  width: 100%;
  display: block;
}
</style>


<script>
import PageTitle from '../PageTitle';
import ProjectCreate from '../projects/ProjectCreate.vue';
import ProjectList from './ProjectList.vue';

export default {
  components: {
    PageTitle,
    ProjectCreate,
    ProjectList
  },

  data() {
    return {
      selectedProject: null,
      removeProjectModal: false,
      removingProject: {
        id: ''
      },
    }
  },

  watch: {
    removeProjectModal(val) {
      val || this.cancelDeletion()
    },
  },

  computed: {
    projects() {
      return this.$store.state.project.memberships
    },
    user() {
      return this.$store.state.user.user
    },
    myProjects() {
      // Filter projects where projects.project.ownerId === user.id      
      return this.projects.filter(project => project.project.ownerId === this.$store.state.user.user.id)
    },
    sharedProjects() {
      // Filter projects where projects.project.ownerId !== user.id
      return this.projects.filter(project => project.project.ownerId !== this.$store.state.user.user.id)

    },
    loading() {
      return this.$store.state.project.loading
    },
    error() {
      return this.$store.state.project.error
    },
    selectedProjectId() {
      return this.$store.state.project.selectedProjectId
    },
    selectedNamespace() {
      return this.$store.state.namespace.selectedProjectId
    },
    // Needed for quota
    billingAccount() {
      return this.$store.state.billing.billingAccount
    },
  },

  mounted() {
    this.$store.dispatch('GetUser').then(() => {
      this.initializeProject()
    })
    
    this.$store.dispatch('GetBillingAccount', {
      full: true,
    })
  },

  methods: {
    refresh() {
      this.$store.dispatch('GetProjectMemberships')
    },
    selectProjectAndGo(selectedProject) {
      this.$store.dispatch('SelectProject', selectedProject.projectId)
      this.$store.dispatch('ResetDevicesList').then(() => {
        this.$router.push({
          name: 'devices',
          params: {
            project: selectedProject.projectId,
          },
        })
      })
    },
    async initializeProject() {
      await this.$store.dispatch('GetProjectMemberships')
      await this.$store.dispatch('LoadSelectedProject')
      if (!this.selectedProjectId) {
        let first = this.$store.getters.getFirstProject()
        this.$store.dispatch('SelectProject', first)
      }

      if (this.projects.length > 0) {
        await this.loadProjecNamespaces()
      }
    },
    async loadProjecNamespaces() {
      let q = {
        project: this.selectedProjectId
      }
      await this.$store.dispatch('ListNamespaces', q)
      this.$store.dispatch('LoadSelectedNamespace')
    }
  }
}
</script>
